"use client";

import { useState, useEffect } from "react";
import { Building, LucideIcon } from "lucide-react";

// ===============================================
// TYPES & INTERFACES
// ===============================================

export interface LogoProps {
  /**
   * Logo image URL
   */
  src: string | undefined | null;
  
  /**
   * Alternative text for the image (also used as fallback description)
   * Defaults to Building icon when image fails to load
   */
  alt?: string;
  
  /**
   * Container dimensions and styling classes
   */
  containerClassName?: string;
  
  /**
   * Image styling classes
   */
  imageClassName?: string;
  
  /**
   * Fallback element styling classes
   */
  fallbackClassName?: string;
  
  /**
   * Custom fallback icon component (defaults to Building)
   */
  fallbackIcon?: LucideIcon;
  
  /**
   * Whether to show fallback while image is loading
   */
  showFallbackWhileLoading?: boolean;
  
  /**
   * Callback fired when image loads successfully
   */
  onLoad?: () => void;
  
  /**
   * Callback fired when image fails to load
   */
  onError?: () => void;
}

// ===============================================
// LOGO COMPONENT
// ===============================================

export const Logo = ({
  src,
  alt = "Logo",
  containerClassName = "w-20 h-20 bg-white rounded-xl shadow-sm flex items-center justify-center",
  imageClassName = "w-16 h-16 object-contain rounded-lg",
  fallbackClassName = "h-10 w-10 text-gray-600",
  fallbackIcon: FallbackIcon = Building,
  showFallbackWhileLoading = false,
  onLoad,
  onError,
}: LogoProps) => {
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const handleError = () => {
    setHasError(true);
    setIsLoading(false);
    onError?.();
  };

  const handleLoad = () => {
    setIsLoading(false);
    onLoad?.();
  };

  useEffect(() => {
    if (src) {
      setHasError(false);
      setIsLoading(true);
    } else {
      setHasError(true);
      setIsLoading(false);
    }
  }, [src]);

  const showLogo = src && !hasError;
  const showFallback = hasError || !src || (showFallbackWhileLoading && isLoading);

  const fallbackElement = <FallbackIcon className={fallbackClassName} aria-label={alt} />;

  return (
    <div className={containerClassName}>
      {showLogo ? (
        <>
          <img
            src={src}
            alt={alt}
            className={`${imageClassName} ${isLoading ? "hidden" : ""}`}
            onError={handleError}
            onLoad={handleLoad}
          />
          {isLoading && showFallbackWhileLoading && fallbackElement}
        </>
      ) : (
        showFallback && fallbackElement
      )}
    </div>
  );
};

// ===============================================
// PRESET SIZES
// ===============================================

export const LogoPresets = {
  small: {
    containerClassName: "w-20 h-20 bg-white rounded-xl shadow-sm flex items-center justify-center",
    imageClassName: "w-16 h-16 object-contain rounded-lg",
    fallbackClassName: "h-10 w-10 text-gray-600",
  },
  large: {
    containerClassName: "w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center",
    imageClassName: "w-14 h-14 object-contain rounded-lg",
    fallbackClassName: "h-8 w-8 text-gray-600",
  },
  medium: {
    containerClassName: "w-24 h-24 bg-white rounded-xl shadow-sm flex items-center justify-center",
    imageClassName: "w-20 h-20 object-contain rounded-lg",
    fallbackClassName: "h-12 w-12 text-gray-600",
  },
};

// ===============================================
// PRESET COMPONENTS
// ===============================================

export const SmallLogo = (props: Omit<LogoProps, "containerClassName" | "imageClassName" | "fallbackClassName">) => (
  <Logo {...LogoPresets.small} {...props} />
);

export const LargeLogo = (props: Omit<LogoProps, "containerClassName" | "imageClassName" | "fallbackClassName">) => (
  <Logo {...LogoPresets.large} {...props} />
);

export const MediumLogo = (props: Omit<LogoProps, "containerClassName" | "imageClassName" | "fallbackClassName">) => (
  <Logo {...LogoPresets.medium} {...props} />
);

